import { Loader, ErrorMessage, SharePreviewCard } from '@communityshare/ui-shared';
import { useConnect } from '@lore/query-connect';
import { PayloadStates } from '@lore/utils';
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import useProjectImage from '@/hooks/useProjectImage';
import useProjectChannel from '@/hooks/useProjectChannel';
import usePreviewImages from '@/features/share-common/hooks/usePreviewImages';
import fallbackImage from '@assets/boy_magnifying_glass_butterfly.jpg';

// Window name for the preview tab: repeat clicks reuse the tab instead of
// opening new ones, and gives the BroadcastChannel peer a known identity.
const PREVIEW_WINDOW_NAME = 'cs-share-project-preview';

ProjectLinkPreview.propTypes = {
  project: PropTypes.shape({
    id: PropTypes.number.isRequired,
    state: PropTypes.string.isRequired,
    data: PropTypes.shape({
      activityId: PropTypes.number,
      siteId: PropTypes.number,
      title: PropTypes.string.isRequired
    }).isRequired
  }).isRequired
};

export default function ProjectLinkPreview(props) {
  const { project } = props;
  const { t } = useTranslation();
  // Default to tag 1 so byId never throws for projects without an activity
  const activity = useConnect('tag.byId', {
    id: project?.data?.activityId ?? 1
  });
  const siteId = project?.data?.siteId ?? null;
  // Pass 0 when siteId is absent so the blueprint doesn't throw 'Missing id';
  // a 0-id query returns NOT_FOUND harmlessly. Location is omitted in the
  // render when siteId is null (see below).
  const site = useConnect('site.byId', {
    id: siteId ?? 0
  });
  const [hostname, setHostname] = useState('...');
  // Local selection; null defers to the saved image (or the default)
  const [selectedImageId, setSelectedImageId] = useState(null);
  const projectImage = useProjectImage(project.id);
  const { loading: imagesLoading, images } = usePreviewImages(project.data.activityId);
  const broadcast = useProjectChannel(project.id);
  const defaultSavedRef = useRef(false);

  useEffect(() => {
    // Get the hostname from the browser window
    setHostname(window.location.hostname);
  }, []);

  // A project with no saved image gets the default persisted once,
  // so the share page renders the same photo the preview shows.
  // Waits for the activity's image set so the right default is saved.
  useEffect(() => {
    if (projectImage.isSuccess && !projectImage.image && !imagesLoading && !defaultSavedRef.current) {
      defaultSavedRef.current = true;
      const image = images[0];
      projectImage.upsert({ imageUrl: image.src, width: image.width, height: image.height });
    }
  }, [projectImage.isSuccess, projectImage.image, imagesLoading]);

  // loading project, its saved share image, or the activity's image set
  if ([PayloadStates.FETCHING, PayloadStates.INITIAL_STATE].includes(project.state) || projectImage.isLoading || imagesLoading) {
    return (
      <div className="flex flex-col justify-center h-[80vh]">
        <Loader/>
      </div>
    )
  }

  // unexpected error state
  if (![PayloadStates.RESOLVED, PayloadStates.UPDATING].includes(project.state) || !project.data) {
    return (
      <div className="flex flex-col items-center">
        <ErrorMessage v2={true}>
          {t('projects.share.data_error')}
        </ErrorMessage>
      </div>
    )
  }

  // Local selection wins (optimistic), then the saved image;
  // null renders the empty state until an image exists
  const savedImage = images.find(image => image.src === projectImage.image?.imageUrl);
  const shareImage = images.find(image => image.id === selectedImageId) ?? savedImage ?? null;

  // The preview swaps immediately while the save runs;
  // a failure surfaces through projectImage.upsertError below.
  function onShareImageChange(imageId) {
    setSelectedImageId(imageId);
    const image = images.find(image => image.id === imageId);
    projectImage.upsert({
      imageUrl: image.src,
      width: image.width,
      height: image.height
    });
    broadcast({ imageUrl: image.src });
  }

  function onPreview() {
    window.open(
      `${window.location.origin}/share/project/${project.id}`,
      `${PREVIEW_WINDOW_NAME}-${project.id}`
    );
  }

  const siteLabel = siteId && site.state !== PayloadStates.FETCHING ?
    ` | ${site?.data?.city ?? ''}, ${site?.data?.state ?? ''}` :
    '';

  // project is loaded or updating (probably saving the title)
  return (
    <SharePreviewCard
      title={t('projects.share.link_preview')}
      imageSrc={shareImage?.src}
      imageKey={shareImage?.id}
      imageAlt={t('projects.share.preview_image_alt')}
      // Curated WordPress URLs can 404; fall back to a bundled image.
      imageFallback={<img src={fallbackImage} className="w-full h-full object-cover" alt="" />}
      urlText={`${hostname}/share/project/${project.id}`}
      eyebrowText={(
        <>
          {activity.state === PayloadStates.FETCHING && <Loader/>}
          {activity?.data?.name ?? '...'}{siteLabel}
        </>
      )}
      primaryText={t('share.common.cta')}
      secondaryText={<>&ldquo;{project.data.title}&rdquo;</>}
      previewLabel={t('projects.share.preview_page')}
      onPreview={onPreview}
      images={images}
      selectedImageId={shareImage?.id ?? null}
      onImageChange={onShareImageChange}
      swapPhotoLabel={t('projects.share.swap_photo')}
      getImageLabel={(image, index) => t('projects.share.photo_option', { number: index + 1 })}
      errorMessage={projectImage.upsertError ? t('projects.share.image_error') : null}
    />
  );
}
